import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Icon, SafeHtmlPipe } from '@millionshow/client-libs';
import { FaqService } from '@millionshow/api-client';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import PaginationComponent from '../../../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../../../components/not-found/not-found.component';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

@Component({
  selector: 'app-participant-faq',
  imports: [
    CommonModule,
    Icon,
    SafeHtmlPipe,
    PaginationComponent,
    NotFoundComponent,
  ],
  templateUrl: './participant-faq.component.html',
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({ height: '0', opacity: '0', overflow: 'hidden' })
      ),
      state(
        'expanded',
        style({ height: '*', opacity: '1', overflow: 'hidden' })
      ),
      transition('collapsed <=> expanded', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
    ]),
    trigger('rotateChevron', [
      state('down', style({ transform: 'rotate(90deg)' })),
      state('up', style({ transform: 'rotate(-90deg)' })),
      transition('down <=> up', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
    ]),
  ],
})
export class ParticipantFaqComponent {
  private readonly faqService = inject<FaqService>(FaqService);

  openStates = signal<Record<string, boolean>>({});
  query = signal<string>('');
  pageNo = signal<number>(1);
  pageSize = signal<number>(10);

  private searchSubject = new Subject<string>();

  constructor() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchQuery => {
        this.query.set(searchQuery);
        this.pageNo.set(1);
      });
  }

  $data = rxResource({
    stream: ({ params }) => {
      return this.faqService.faqControllerSearchV1({
        ...params,
        isExposed: true,
        target: 'PARTICIPANT' as const,
        type: 'AGRITEX' as const,
      });
    },
    params: () => ({
      pageNo: this.pageNo(),
      pageSize: this.pageSize(),
      query: this.query(),
    }),
  });

  faqs = computed(() => {
    const data = this.$data.value();
    return data?.items || [];
  });

  totalCount = computed(() => {
    return this.$data.value()?.pageInfo?.totalItems || 0;
  });

  currentPage = computed(() => this.pageNo());

  totalPages = computed<number[]>(() => {
    const totalItems = this.$data.value()?.pageInfo?.totalItems || 0;
    const count = Math.max(1, Math.ceil(totalItems / this.pageSize()));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  toggleFaq(id: string) {
    this.openStates.update(states => ({
      ...states,
      [id]: !states[id],
    }));
  }

  isOpen(id: string): boolean {
    return this.openStates()[id] || false;
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    const faqSection = document.querySelector('.faq-section');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
